import { createContext, type FC, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthProvider";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { localStorageService } from "../services/LocalStorageService";

interface RevealWageContextValue {
	viewWageAllowed: boolean;
	revealWage: boolean;
	setRevealWage: (value: boolean) => void;
}

const RevealWageContext = createContext<RevealWageContextValue | null>(null);

export const RevealWageProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const {
		userInfo: { id: userId },
	} = useAuth();
	const { chosenDepartment } = useChosenDepartment();
	const hideWageFromPermissions = chosenDepartment.hide_wage;

	const [revealWage, setRevealWageState] = useState<boolean>(() => {
		if (!userId || hideWageFromPermissions) {
			return false;
		}
		return localStorageService.getRevealWage(userId, hideWageFromPermissions);
	});

	useEffect(() => {
		/**
		 * If the uid is changed mid session (e.g. user logs out and another logs in),
		 * we need to reset the reveal wage state to the new user's preference.
		 */
		if (!userId || hideWageFromPermissions) {
			setRevealWageState(false);
			return;
		}
		setRevealWageState(localStorageService.getRevealWage(userId, hideWageFromPermissions));
	}, [userId, hideWageFromPermissions]);

	const setRevealWage = useCallback(
		(value: boolean) => {
			setRevealWageState(value);
			if (userId) {
				localStorageService.setRevealWage(value, userId);
			}
		},
		[userId],
	);

	const canViewWage = useMemo(() => {
		if (hideWageFromPermissions) return false;
		return revealWage;
	}, [hideWageFromPermissions, revealWage]);

	const contextValue = useMemo<RevealWageContextValue>(
		() => ({
			viewWageAllowed: !hideWageFromPermissions,
			revealWage: canViewWage,
			setRevealWage,
		}),
		[canViewWage, setRevealWage, hideWageFromPermissions],
	);

	return <RevealWageContext.Provider value={contextValue}>{children}</RevealWageContext.Provider>;
};

export const useRevealWage = (): boolean => {
	const context = useContext(RevealWageContext);
	if (!context) {
		throw new Error("useRevealWage must be used within a RevealWageProvider");
	}
	return context.revealWage;
};

/**
 * Whether this user is PERMITTED to see wage data here — the permission alone, with no regard for the
 * reveal toggle. Distinct from `useRevealWage`, which also answers "and have they toggled it on right
 * now": that toggle is a screen-privacy convenience persisted in localStorage, so it must not decide
 * anything durable (what a scheduled report contains, what gets persisted server-side).
 */
export const useViewWageAllowed = (): boolean => {
	const context = useContext(RevealWageContext);
	if (!context) {
		throw new Error("useViewWageAllowed must be used within a RevealWageProvider");
	}
	return context.viewWageAllowed;
};

export const useRevealWageActions = () => {
	const context = useContext(RevealWageContext);
	if (!context) {
		throw new Error("useRevealWageActions must be used within a RevealWageProvider");
	}
	return context;
};
